import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { clients, chefsEquipe, techniciens } from '../utils/mockData';
import { ArrowLeft, Briefcase, Calendar, Users, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WorkType, Priority } from '../utils/types';
const workTypeLabels: Record<WorkType, string> = {
  signaletique_interieure: 'Signalétique intérieure',
  signaletique_exterieure: 'Signalétique extérieure',
  marquage_sol: 'Marquage au sol',
  revetement: 'Revêtement / Résine',
  peinture_decorative: 'Peinture décorative',
  mobilier: 'Installation mobilier',
  amenagement_commercial: 'Aménagement commercial',
  maintenance_preventive: 'Maintenance préventive',
  intervention_corrective: 'Intervention corrective',
  renovation_partielle: 'Rénovation partielle'
};
export function NewChantier() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    clientId: '',
    name: '',
    location: '',
    city: '',
    country: 'France',
    workType: '' as WorkType | '',
    priority: 'normal' as Priority,
    description: '',
    startDate: '',
    endDate: '',
    status: 'a_planifier' as const,
    chefEquipeId: '',
    teamIds: [] as string[]
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validateStep1 = () => {
    const e: Record<string, string> = {};
    if (!formData.clientId) e.clientId = 'Sélectionnez un client';
    if (!formData.name.trim()) e.name = 'Nom requis';
    if (!formData.location.trim()) e.location = 'Adresse requise';
    if (!formData.city.trim()) e.city = 'Ville requise';
    if (!formData.workType) e.workType = 'Type de travaux requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateStep2 = () => {
    const e: Record<string, string> = {};
    if (!formData.startDate) e.startDate = 'Date de début requise';
    if (!formData.endDate) e.endDate = 'Date de fin requise';
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) e.endDate = 'La date de fin doit être après le début';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateStep3 = () => {
    const e: Record<string, string> = {};
    if (!formData.chefEquipeId) e.chefEquipeId = "Un chef d'équipe doit être assigné";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const handleNext = () => {
    let valid = false;
    if (step === 1) valid = validateStep1();else if (step === 2) valid = validateStep2();else if (step === 3) valid = validateStep3();
    if (valid && step < 3) setStep(step + 1);else if (valid && step === 3) {
      setShowSuccess(true);
      setTimeout(() => navigate('/chantiers'), 2000);
    }
  };
  const toggleTeam = (id: string) => {
    setFormData(p => ({
      ...p,
      teamIds: p.teamIds.includes(id) ? p.teamIds.filter(x => x !== id) : [...p.teamIds, id]
    }));
  };
  const inputClass = (field: string) => `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1DBAB4] focus:border-transparent outline-none ${errors[field] ? 'border-red-300' : 'border-gray-300'}`;
  return <div className="min-h-screen bg-gray-50 pb-20 md:pb-6" data-id="element-677">
      <div className="page-container section-spacing" data-id="element-678">
        <Button variant="ghost" size="sm" className="pl-0" onClick={() => navigate(-1)} leftIcon={<ArrowLeft size={16} data-id="element-680" />} data-id="element-679">
          Retour
        </Button>
        <div data-id="element-681">
          <h1 className="heading-xl" data-id="element-682">Nouveau Chantier</h1>
          <p className="text-caption mt-1" data-id="element-683">Créer un nouveau dossier chantier</p>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-between mb-8" data-id="element-684">
          {[1, 2, 3].map(s => <div key={s} className="flex items-center flex-1" data-id="element-685">
              <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-bold text-sm sm:text-base transition-all ${s <= step ? 'bg-[#1DBAB4] text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-686">
                {s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 mx-1 sm:mx-2 transition-all ${s < step ? 'bg-[#1DBAB4]' : 'bg-gray-200'}`} data-id="element-687" />}
            </div>)}
        </div>

        <AnimatePresence mode="wait" data-id="element-688">
          {step === 1 && <motion.div key="s1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-689">
              <Card className="p-4 sm:p-6 space-y-4" data-id="element-690">
                <div className="flex items-center gap-3 mb-2" data-id="element-691">
                  <div className="p-2 bg-teal-50 rounded-lg" data-id="element-692">
                    <Briefcase className="w-5 h-5 text-teal-700" data-id="element-693" />
                  </div>
                  <div data-id="element-694">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-695">
                      Informations du Chantier
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-696">
                      Client, localisation et type de travaux
                    </p>
                  </div>
                </div>
                <div data-id="element-697">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-698">
                    Client *
                  </label>
                  <select value={formData.clientId} onChange={e => setFormData({
                ...formData,
                clientId: e.target.value
              })} className={inputClass('clientId')} data-id="element-699">
                    <option value="" data-id="element-700">Sélectionner un client</option>
                    {clients.map(c => <option key={c.id} value={c.id} data-id="element-701">
                        {c.raisonSociale}
                      </option>)}
                  </select>
                  {errors.clientId && <p className="text-red-600 text-sm mt-1" data-id="element-702">
                      {errors.clientId}
                    </p>}
                </div>
                <div data-id="element-703">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-704">
                    Nom du chantier *
                  </label>
                  <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className={inputClass('name')} placeholder="Ex: Parking Indigo Gare du Nord - Signalétique" data-id="element-705" />
                  {errors.name && <p className="text-red-600 text-sm mt-1" data-id="element-706">{errors.name}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-707">
                  <div data-id="element-708">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-709">
                      Adresse *
                    </label>
                    <input type="text" value={formData.location} onChange={e => setFormData({
                  ...formData,
                  location: e.target.value
                })} className={inputClass('location')} data-id="element-710" />
                    {errors.location && <p className="text-red-600 text-sm mt-1" data-id="element-711">
                        {errors.location}
                      </p>}
                  </div>
                  <div data-id="element-712">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-713">
                      Ville *
                    </label>
                    <input type="text" value={formData.city} onChange={e => setFormData({
                  ...formData,
                  city: e.target.value
                })} className={inputClass('city')} data-id="element-714" />
                    {errors.city && <p className="text-red-600 text-sm mt-1" data-id="element-715">{errors.city}</p>}
                  </div>
                </div>
                <div data-id="element-716">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-717">
                    Type de travaux *
                  </label>
                  <select value={formData.workType} onChange={e => setFormData({
                ...formData,
                workType: e.target.value as WorkType
              })} className={inputClass('workType')} data-id="element-718">
                    <option value="" data-id="element-719">Sélectionner</option>
                    {Object.entries(workTypeLabels).map(([k, v]) => <option key={k} value={k} data-id="element-720">
                        {v}
                      </option>)}
                  </select>
                  {errors.workType && <p className="text-red-600 text-sm mt-1" data-id="element-721">
                      {errors.workType}
                    </p>}
                </div>
                <div data-id="element-722">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-723">
                    Priorité
                  </label>
                  <div className="flex gap-3" data-id="element-724">
                    {(['normal', 'urgent', 'critique'] as Priority[]).map(p => <label key={p} className={`flex-1 text-center px-3 py-2 border rounded-lg cursor-pointer transition-all text-sm font-medium ${formData.priority === p ? 'border-[#1DBAB4] bg-teal-50 text-teal-800' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-725">
                          <input type="radio" name="priority" value={p} checked={formData.priority === p} onChange={() => setFormData({
                    ...formData,
                    priority: p
                  })} className="sr-only" data-id="element-726" />
                          {p === 'normal' ? 'Normal' : p === 'urgent' ? 'Urgent' : 'Critique'}
                        </label>)}
                  </div>
                </div>
                <div data-id="element-727">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-728">
                    Description
                  </label>
                  <textarea value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1DBAB4] focus:border-transparent outline-none" rows={3} placeholder="Détails de l'intervention..." data-id="element-729" />
                </div>
              </Card>
            </motion.div>}
          {step === 2 && <motion.div key="s2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-730">
              <Card className="p-4 sm:p-6 space-y-4" data-id="element-731">
                <div className="flex items-center gap-3 mb-2" data-id="element-732">
                  <div className="p-2 bg-teal-50 rounded-lg" data-id="element-733">
                    <Calendar className="w-5 h-5 text-teal-700" data-id="element-734" />
                  </div>
                  <div data-id="element-735">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-736">
                      Planning
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-737">
                      Dates et statut initial
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-738">
                  <div data-id="element-739">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-740">
                      Date de début *
                    </label>
                    <input type="date" value={formData.startDate} onChange={e => setFormData({
                  ...formData,
                  startDate: e.target.value
                })} className={inputClass('startDate')} data-id="element-741" />
                    {errors.startDate && <p className="text-red-600 text-sm mt-1" data-id="element-742">
                        {errors.startDate}
                      </p>}
                  </div>
                  <div data-id="element-743">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-744">
                      Date de fin estimée *
                    </label>
                    <input type="date" value={formData.endDate} onChange={e => setFormData({
                  ...formData,
                  endDate: e.target.value
                })} className={inputClass('endDate')} data-id="element-745" />
                    {errors.endDate && <p className="text-red-600 text-sm mt-1" data-id="element-746">
                        {errors.endDate}
                      </p>}
                  </div>
                </div>
              </Card>
            </motion.div>}
          {step === 3 && <motion.div key="s3" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-747">
              <Card className="p-4 sm:p-6 space-y-4" data-id="element-748">
                <div className="flex items-center gap-3 mb-2" data-id="element-749">
                  <div className="p-2 bg-teal-50 rounded-lg" data-id="element-750">
                    <Users className="w-5 h-5 text-teal-700" data-id="element-751" />
                  </div>
                  <div data-id="element-752">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-753">Équipe</h2>
                    <p className="text-sm text-gray-500" data-id="element-754">
                      Chef d'équipe et techniciens
                    </p>
                  </div>
                </div>
                <div data-id="element-755">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-756">
                    Chef d'équipe *
                  </label>
                  <div className="space-y-2" data-id="element-757">
                    {chefsEquipe.map(c => <label key={c.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${formData.chefEquipeId === c.id ? 'border-[#1DBAB4] bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-758">
                        <input type="radio" name="chef" value={c.id} checked={formData.chefEquipeId === c.id} onChange={() => setFormData({
                    ...formData,
                    chefEquipeId: c.id
                  })} className="text-[#1DBAB4]" data-id="element-759" />
                        <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full" data-id="element-760" />
                        <div data-id="element-761">
                          <p className="font-medium text-gray-900" data-id="element-762">{c.name}</p>
                          <p className="text-sm text-gray-500" data-id="element-763">Chef d'équipe</p>
                        </div>
                      </label>)}
                  </div>
                  {errors.chefEquipeId && <p className="text-red-600 text-sm mt-2" data-id="element-764">
                      {errors.chefEquipeId}
                    </p>}
                </div>
                <div data-id="element-765">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-766">
                    Techniciens
                  </label>
                  <div className="space-y-2" data-id="element-767">
                    {techniciens.map(t => <label key={t.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${formData.teamIds.includes(t.id) ? 'border-[#1DBAB4] bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-768">
                        <input type="checkbox" checked={formData.teamIds.includes(t.id)} onChange={() => toggleTeam(t.id)} className="text-[#1DBAB4] rounded" data-id="element-769" />
                        <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full" data-id="element-770" />
                        <div data-id="element-771">
                          <p className="font-medium text-gray-900" data-id="element-772">{t.name}</p>
                          <p className="text-sm text-gray-500" data-id="element-773">Technicien</p>
                        </div>
                      </label>)}
                  </div>
                </div>
              </Card>
            </motion.div>}
        </AnimatePresence>

        <div className="sticky bottom-20 md:bottom-0 md:static bg-white md:bg-transparent p-4 md:p-0 -mx-4 md:mx-0 border-t md:border-t-0 border-gray-200 md:border-0 shadow-lg md:shadow-none flex items-center justify-between gap-3" data-id="element-774">
          <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} className="flex-1 sm:flex-initial" data-id="element-775">
            {step > 1 ? 'Précédent' : 'Annuler'}
          </Button>
          <Button onClick={handleNext} className="flex-1 sm:flex-initial" data-id="element-776">
            {step < 3 ? 'Suivant' : 'Créer le chantier'}
          </Button>
        </div>

        <AnimatePresence data-id="element-777">
          {showSuccess && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-778">
              <motion.div initial={{
            scale: 0.9
          }} animate={{
            scale: 1
          }} className="bg-white rounded-2xl p-8 max-w-md text-center" data-id="element-779">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-780">
                  <CheckCircle className="w-8 h-8 text-green-600" data-id="element-781" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-782">
                  Chantier créé !
                </h3>
                <p className="text-gray-600" data-id="element-783">
                  Le chantier "{formData.name}" a été créé avec succès.
                </p>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}